/**
 * The one date formatter in the product.
 *
 * Two rules this module exists to enforce:
 *
 * 1. Numeric dates are YEAR-FIRST in every locale, with locale-specific
 *    separators. `Intl` cannot express this — `Intl.DateTimeFormat('de')`
 *    insists on `04.06.2026` — so numeric output is built from an explicit
 *    pattern table. Only the prose (`long`) variant delegates to `Intl`, and
 *    it does so with a PINNED rendering locale, never the caller's.
 *
 * 2. The caller's locale is normalized to its primary subtag first. webclient
 *    initialises i18next-browser-languagedetector with no `supportedLngs` and
 *    no `load: 'languageOnly'`, so `i18n.language` is whatever the browser
 *    reported — `en-US`, `de-AT`, `hu-HU`. Without normalization an American
 *    browser would drag American formatting back into the prose variant.
 *
 * Calendar days vs instants: a bare `YYYY-MM-DD` communicates a specific
 * calendar day (a delivery date, an order date). Per ECMAScript,
 * `new Date("2026-06-04")` parses as UTC midnight, which `Intl` then renders
 * in the runtime's local timezone — shifting the displayed day backwards for
 * viewers west of UTC (June 4 showing as June 3 in Los Angeles). Showing the
 * wrong day is unacceptable for a committed date, so bare dates are built
 * from local Y/M/D parts instead. Full ISO instants are converted to the
 * viewer's timezone as normal.
 *
 * The format table is pinned in common-contracts/date_format.json and
 * asserted from both stacks. Changing a value here without changing it there
 * fails the contract test, by design.
 *
 * An optional `timeZone` renders instants in a named IANA zone (the shop's
 * `Account#time_zone`) instead of the viewer's; calendar-day inputs are
 * unaffected.
 */

export type DateInput = string | Date | null | undefined;
export type DateStyle = 'numeric' | 'long' | 'full';

export interface FormatOptions {
  /** IANA zone to render instants in. Omit for the runtime's local zone. */
  readonly timeZone?: string;
}

type SupportedLocale = 'en' | 'de' | 'hu';

interface LocaleFormat {
  /** Separator between the year, month and day parts (always in that order). */
  readonly separator: string;
  /** Trailing characters after the final part (Hungarian orthography). */
  readonly suffix: string;
  /** Pinned locale handed to Intl for the prose variant. */
  readonly renderingLocale: string;
}

const FORMATS: Record<SupportedLocale, LocaleFormat> = {
  en: { separator: '-', suffix: '', renderingLocale: 'en-GB' },
  de: { separator: '.', suffix: '', renderingLocale: 'de-DE' },
  hu: { separator: '. ', suffix: '.', renderingLocale: 'hu-HU' },
};

const FALLBACK_LOCALE: SupportedLocale = 'en';

const isSupported = (value: string): value is SupportedLocale =>
  Object.prototype.hasOwnProperty.call(FORMATS, value);

const formatFor = (locale: string): LocaleFormat => {
  const primary = (locale ?? '').split('-')[0].toLowerCase();
  return FORMATS[isSupported(primary) ? primary : FALLBACK_LOCALE];
};

/** A bare calendar day: no time, no zone, must not be shifted. */
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** A bare month bucket, as emitted by the analytics endpoints. */
const CALENDAR_MONTH = /^(\d{4})-(\d{2})$/;

const toDate = (value: DateInput): { date: Date; isInstant: boolean } | null => {
  if (value == null) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { date: value, isInstant: true };
  }

  const raw = value.trim();
  if (!raw) return null;

  const day = CALENDAR_DAY.exec(raw);
  if (day) {
    const year = Number(day[1]);
    const monthIndex = Number(day[2]) - 1;
    const date = Number(day[3]);
    const d = new Date(year, monthIndex, date);
    // new Date() rolls invalid components over (e.g. Feb 30 -> Mar 2) rather
    // than rejecting them, so this is the one place we have to validate by
    // hand: confirm the constructed date's parts match what was parsed.
    const valid = d.getFullYear() === year && d.getMonth() === monthIndex && d.getDate() === date;
    return valid ? { date: d, isInstant: false } : null;
  }

  const month = CALENDAR_MONTH.exec(raw);
  if (month) {
    const year = Number(month[1]);
    const monthIndex = Number(month[2]) - 1;
    const d = new Date(year, monthIndex, 1);
    const valid = d.getFullYear() === year && d.getMonth() === monthIndex;
    return valid ? { date: d, isInstant: false } : null;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : { date: parsed, isInstant: true };
};

const pad = (n: number): string => String(n).padStart(2, '0');

interface Parts { year: number; month: number; day: number; hour: number; minute: number }

const localParts = (d: Date): Parts => ({
  year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(),
});

/** Fixed locale used only to extract zone-shifted date parts below — its
 * output is never rendered to the user, so any locale works. */
const PARTS_LOCALE = 'en-GB';

const zonedParts = (d: Date, timeZone: string): Parts => {
  // A caller-supplied timeZone (Account#time_zone) is validated as an IANA
  // identifier at write time, but ICU's own zone database can still reject a
  // value the writer's TZInfo/ActiveSupport accepted (version skew between
  // the two databases), and `Intl.DateTimeFormat` throws a RangeError for an
  // unknown zone rather than returning something. Falling back to the
  // viewer's local zone keeps a render from ever crashing on a bad zone.
  try {
    const dtf = new Intl.DateTimeFormat(PARTS_LOCALE, {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
    const byType = new Map(dtf.formatToParts(d).map((p) => [p.type, p.value]));
    const get = (type: Intl.DateTimeFormatPartTypes): number => Number(byType.get(type) ?? '0');
    return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute') };
  } catch {
    return localParts(d);
  }
};

/** `isInstant` is false for bare YYYY-MM-DD / YYYY-MM inputs, which never shift. */
const partsOf = (d: Date, isInstant: boolean, options?: FormatOptions): Parts =>
  isInstant && options?.timeZone ? zonedParts(d, options.timeZone) : localParts(d);

const numericDate = (p: Parts, f: LocaleFormat): string =>
  [String(p.year), pad(p.month), pad(p.day)].join(f.separator) + f.suffix;

const clockTime = (p: Parts): string => `${pad(p.hour)}:${pad(p.minute)}`;

/**
 * Format a date. `numeric` (the default) is year-first per the pinned table;
 * `long` is locale-native prose. Returns '' for absent or malformed input, so
 * callers that skip rendering can simply test falsiness.
 */
export function formatDate(value: DateInput, locale: string, style: DateStyle = 'numeric', options?: FormatOptions): string {
  const parsed = toDate(value);
  if (!parsed) return '';

  const f = formatFor(locale);
  if (style !== 'numeric') {
    // 'long' and 'full' are both Intl dateStyle values. 'full' prepends the
    // weekday, which the in-person handover callout needs: a customer reading
    // "collect it on the 4th" plans around the day of the week, not the date.
    const timeZone = parsed.isInstant ? options?.timeZone : undefined;
    return new Intl.DateTimeFormat(f.renderingLocale, { dateStyle: style, timeZone }).format(parsed.date);
  }
  return numericDate(partsOf(parsed.date, parsed.isInstant, options), f);
}

/** Numeric date plus 24-hour clock time. */
export function formatDateTime(value: DateInput, locale: string, options?: FormatOptions): string {
  const parsed = toDate(value);
  if (!parsed) return '';
  const p = partsOf(parsed.date, parsed.isInstant, options);
  return `${numericDate(p, formatFor(locale))} ${clockTime(p)}`;
}

/**
 * 24-hour clock time. Takes no locale: the format is identical in all three,
 * and an unused parameter fails the build under `noUnusedParameters`.
 */
export function formatTime(value: DateInput, options?: FormatOptions): string {
  const parsed = toDate(value);
  return parsed ? clockTime(partsOf(parsed.date, parsed.isInstant, options)) : '';
}

/** Year and month only — for analytics bucket labels. */
export function formatMonth(value: DateInput, locale: string, options?: FormatOptions): string {
  const parsed = toDate(value);
  if (!parsed) return '';
  const f = formatFor(locale);
  const p = partsOf(parsed.date, parsed.isInstant, options);
  return [String(p.year), pad(p.month)].join(f.separator) + f.suffix;
}

/**
 * Wall-clock `YYYY-MM-DDTHH:mm` of an instant in `timeZone` — the value an
 * `<input type="datetime-local">` shows and edits. Bare calendar days are
 * anchored like everywhere else. '' for absent or malformed input.
 */
export function toZonedLocalInput(value: DateInput, timeZone?: string): string {
  const parsed = toDate(value);
  if (!parsed) return '';
  const p = partsOf(parsed.date, parsed.isInstant, { timeZone });
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * Range-checks the parsed digit groups from LOCAL_INPUT: month 1-12, hour
 * 0-23, minute 0-59, and day validated against its own month/leap-year
 * (mirrors toDate's CALENDAR_DAY rollover check — new Date() rolls Feb 30
 * over into Mar 2 rather than rejecting it, so the constructed UTC date's
 * parts are compared back against what was asked for).
 */
const validLocalParts = (year: number, month: number, day: number, hour: number, minute: number): boolean => {
  if (month < 1 || month > 12 || hour < 0 || hour > 23 || minute < 0 || minute > 59) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
};

/**
 * Inverse of toZonedLocalInput: a datetime-local value read as wall-clock
 * time in `timeZone`, returned as a UTC ISO instant. The zone offset is
 * resolved by measuring what zonedParts() shows for a first guess and
 * correcting once more for a DST boundary — no separate Intl call, so the
 * drift guard's single exemption still covers all zone math. Without a
 * timeZone the runtime's local zone applies. null for a malformed value.
 */
export function fromZonedLocalInput(local: string, timeZone?: string): string | null {
  const m = LOCAL_INPUT.exec((local ?? '').trim());
  if (!m) return null;
  const [year, month, day, hour, minute] = m.slice(1).map(Number);
  if (!validLocalParts(year, month, day, hour, minute)) return null;
  if (!timeZone) {
    const d = new Date(year, month - 1, day, hour, minute);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  const wanted = Date.UTC(year, month - 1, day, hour, minute);
  let guess = wanted;
  for (let i = 0; i < 2; i += 1) {
    const p = zonedParts(new Date(guess), timeZone);
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    if (shown === wanted) break;
    guess -= shown - wanted;
  }
  return new Date(guess).toISOString();
}

/**
 * A calendar month heading — "September 2026", hu "2026. szeptember" — in the
 * pinned rendering locale. `month` is 1-based. Anchored at UTC noon of the
 * 1st and rendered in UTC so no runtime zone can shift it into the previous
 * month.
 */
export function formatMonthYear(year: number, month: number, locale: string): string {
  const f = formatFor(locale);
  const anchor = new Date(Date.UTC(year, month - 1, 1, 12));
  return new Intl.DateTimeFormat(f.renderingLocale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(anchor);
}

/** 2024-01-01 is a Monday; the seven days from it label a Monday-first week. */
const MONDAY_ANCHOR_UTC = Date.UTC(2024, 0, 1, 12);

/**
 * Short weekday labels for a Monday-first week (`['Mon', …, 'Sun']`, hu
 * `['H', 'K', 'Sze', …]`) in the pinned rendering locale.
 */
export function weekdayNames(locale: string): string[] {
  const f = formatFor(locale);
  const dtf = new Intl.DateTimeFormat(f.renderingLocale, { weekday: 'short', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => dtf.format(new Date(MONDAY_ANCHOR_UTC + i * 86_400_000)));
}
